import { useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import candidateAnalyticsService from '../services/candidateAnalyticsService';
import fallbackAnalyticsService from '../utils/fallbackAnalytics';

/**
 * Custom hook for tracking candidate search analytics
 * Centralizes analytics tracking logic
 */
export const useCandidateAnalytics = () => {
  const { user, userProfile } = useAuth();
  
  // Track page view on component mount
  useEffect(() => {
    try {
      candidateAnalyticsService?.trackPageView?.('candidate_search');
      
      return () => {
        // Track exit on component unmount
        candidateAnalyticsService?.trackPageExit?.('candidate_search');
      };
    } catch (error) {
      console.error('[Analytics Error]', error);
      return () => {};
    }
  }, []);
  
  // Track page view manually
  const trackPageView = useCallback((pageName = 'candidate_search') => {
    try {
      candidateAnalyticsService?.trackPageView?.(pageName);
    } catch (error) {
      console.error('[Analytics Error]', error);
      fallbackAnalyticsService.trackPageView(pageName);
    }
  }, []);
  
  // Track search operation
  const trackSearch = useCallback((searchData) => {
    try {
      candidateAnalyticsService?.trackSearch?.(searchData);
    } catch (error) {
      console.error('[Analytics Error]', error);
      fallbackAnalyticsService.trackSearch(searchData);
    }
  }, []);
  
  // Track profile view
  const trackProfileView = useCallback((candidateId, source = 'search') => {
    try {
      candidateAnalyticsService?.trackProfileView?.(candidateId, source);
    } catch (error) {
      console.error('[Analytics Error]', error);
      fallbackAnalyticsService.trackProfileView(candidateId, source);
    }
  }, []);
  
  // Track save/unsave operation
  const trackSaveCandidate = useCallback((candidateId, isSaving) => {
    try {
      candidateAnalyticsService?.trackSaveCandidate?.(candidateId, isSaving);
    } catch (error) {
      console.error('[Analytics Error]', error);
      fallbackAnalyticsService.trackSaveCandidate(candidateId, isSaving);
    }
  }, []);
  
  // Track contact operation
  const trackContactCandidate = useCallback((candidateId) => {
    if (!user?.id) return;
    
    try {
      candidateAnalyticsService?.trackContactCandidate?.(candidateId, user.id);
    } catch (error) {
      console.error('[Analytics Error]', error);
      fallbackAnalyticsService.trackContactCandidate(candidateId, user.id);
    }
  }, [user?.id]);
  
  // Track filter usage
  const trackFilterUse = useCallback((filterType, filterValue) => {
    candidateAnalyticsService.trackFilterUse(filterType, filterValue);
  }, []);
  
  // Track view mode change
  const trackViewModeChange = useCallback((viewMode) => {
    candidateAnalyticsService.trackViewModeChange(viewMode);
  }, []);
  
  // Track load more operation
  const trackLoadMore = useCallback((page, totalLoaded) => {
    candidateAnalyticsService.trackLoadMore(page, totalLoaded);
  }, []);
  
  // Track clear filters operation
  const trackClearFilters = useCallback(() => {
    candidateAnalyticsService.trackClearFilters();
  }, []);
  
  // Add user context to all tracking
  useEffect(() => {
    if (user?.id) {
      // Set user context for analytics
      window.analytics?.identify?.(user.id, {
        email: user.email,
        role: userProfile?.role || 'unknown',
        company: userProfile?.company_name || '',
        plan: userProfile?.subscription_plan || 'free'
      });
    }
  }, [user, userProfile]);
  
  return {
    trackSearch,
    trackProfileView,
    trackSaveCandidate,
    trackContactCandidate,
    trackFilterUse,
    trackViewModeChange,
    trackLoadMore,
    trackClearFilters,
    trackPageView
  };
};

export default useCandidateAnalytics;